import { DOC_TYPES, LOCATIONS_SLUG, MENU_SLUG } from '@/data'
import { geti18nText } from '@/hooks/use-i18n'
import { SectionsSchema } from '@/types/components/SectionsProps'

import { getMenuItemListingGroupId } from '@/utils/urls'
import { getSectionsByFallbackRegions } from '@/utils/regions'
import { getOpenRoleCounts, type PhenomJobCounts } from '@/data/phenom'

const LOCATIONS_3UP_OPEN_ROLES_TYPE = 'locations3UpOpenRoles'

const resolveOpenRolesCount = (
  item: SanityLocations3UpOpenRolesItem,
  counts: PhenomJobCounts,
): number => {
  const key = item.location?.phenomCompanyKey?.trim()
  if (!key) return item.openingsCount ?? 0
  return counts[key] ?? 0
}

const augmentSectionWithCounts = (
  section: SanityLocations3UpOpenRoles,
  counts: PhenomJobCounts,
): SanityLocations3UpOpenRoles => ({
  ...section,
  items: section.items?.map(item => ({
    ...item,
    openingsCount: resolveOpenRolesCount(item, counts),
  })),
})

const hasOpenRolesSection = (sections: SectionsSchema | null | undefined): boolean =>
  Boolean(sections?.some(entry => entry?.section?.[0]?._type === LOCATIONS_3UP_OPEN_ROLES_TYPE))

export const augmentSectionsWithOpenRoleCounts = async (
  sections: SectionsSchema | null | undefined,
): Promise<SectionsSchema | null | undefined> => {
  if (!hasOpenRolesSection(sections)) return sections

  // Catch here (not inside the cached loader) so a failed fetch is not stored
  // in unstable_cache for the full 24h revalidate window. Empty counts trigger
  // the per-item manual fallback for this render only; the next render retries.
  let counts: PhenomJobCounts = {}
  try {
    counts = await getOpenRoleCounts()
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown error'
    console.warn(`[phenom] feed unavailable (${message}); falling back to manual openings counts`)
  }

  return sections?.map(entry => {
    const inner = entry?.section?.[0]
    if (inner?._type !== LOCATIONS_3UP_OPEN_ROLES_TYPE) return entry
    return {
      ...entry,
      section: [augmentSectionWithCounts(inner as SanityLocations3UpOpenRoles, counts)],
    }
  })
}

export const formatPageSections = (sections: SectionsSchema) => {
  if (!sections?.length) return sections

  // eslint-disable-next-line
  const sectionsFormatted: SectionsSchema = []

  // eslint-disable-next-line
  sections.forEach((section: any) => {
    if (section.draft) {
      sectionsFormatted.push(section.draft)
    } else {
      sectionsFormatted.push(section)
    }
  })

  return sectionsFormatted as SectionsSchema
}

export const getSections = (data: SanityPage, region: string, fallbackRegions: string[]) => {
  const sectionsByRegion = getSectionsByFallbackRegions(data.sections, fallbackRegions, region)

  if (data._type === 'page') {
    if (sectionsByRegion) return sectionsByRegion
  }

  if (data._type === 'menuItem' || data._type === 'location' || data._type === 'blogPost') {
    const newSections: SectionsSchema = []

    if (data._type === 'menuItem') {
      const title = data?.title || ''

      const allowedStaggeredImages: SanityStaggeredItem[] = []
      let combinedImages: SanityImage[] = []

      if (data?.menuItemData?.lifestyleImages?.length) {
        combinedImages = [...combinedImages, ...data?.menuItemData?.lifestyleImages]
      }

      if (data?.menuItemData?.images?.length) {
        combinedImages = [...combinedImages, ...data?.menuItemData?.images]
      }

      const mainImage = combinedImages[0]

      if (data?.menuItemData?.lifestyleImages?.length) {
        data?.menuItemData?.lifestyleImages.forEach(image => {
          if (image?.asset?.url !== mainImage?.asset?.url) {
            allowedStaggeredImages.push({ mediaType: 'image', image })
          }
        })
      }

      const menuItemHero = {
        section: [
          {
            _type: 'textAndImageHeroWithBreadcrumbs',
            _id: 'textAndImageHeroWithBreadcrumbs',
            cmsSettings: {
              isHidden: false,
              cmsTitle: '',
            },
            sectionBackground: 'dough' as SectionBackground,
            title: title,
            subtitle: data?.menuItemData?.subtitle || '',
            isNew: data?.menuItemData?.isNew || false,
            description: data?.menuItemData?.description || '',
            disclaimers: data?.menuItemData?.disclaimers || [],
            images: combinedImages,
            slug: data?.slug?.current,
            mediaStyle: data?.menuItemData?.heroImageStyle || 'roundedCorners',
            breadcrumbLink1: {
              label: geti18nText(region, 'menu'),
              linkType: 'internal',
              link: {
                _type: DOC_TYPES.PAGE,
                slug: 'menu',
              },
            },
            breadcrumbLink2: {
              label: data?.menuItemData?.category?.title || title,
              linkType: 'internal',
              link: {
                _type: DOC_TYPES.PAGE,
                slug: MENU_SLUG,
              },
              hash: data?.menuItemData?.category?.slug?.current
                ? getMenuItemListingGroupId(data?.menuItemData?.category?.slug?.current as string)
                : null,
              navigationOffset: true,
            },
            staggeredImages: data?.staggeredMediaContent?.items?.length
              ? data?.staggeredMediaContent?.items
              : allowedStaggeredImages,
            cta: {
              label: geti18nText(region, 'findALocation'),
              linkType: 'internal',
              link: {
                _type: DOC_TYPES.PAGE,
                slug: process.env.NEXT_PUBLIC_IS_CANADA_WEBSITE === 'true' ? 'locations/vancouver' : 'locations',
              },
            },
          },
        ],
      }

      newSections.push(menuItemHero)

      if (data?.textAndImageContent?.isEnabled) {
        let defaultLink: SanityLink = {
          label: geti18nText(region, 'menuTextAndImageDefaultLinkTitle'),
          linkType: 'internal',
          link: {
            slug: LOCATIONS_SLUG,
            _type: DOC_TYPES.PAGE,
          },
        }

        if (data?.textAndImageContent?.cta?.length) {
          defaultLink = data?.textAndImageContent?.cta[0]
        }

        const isArch =
          data?.menuItemData?.heroImageStyle === 'square' || data?.menuItemData?.heroImageStyle === 'roundedCorners'

        const textAndImageData = {
          section: [
            {
              _type: 'textAndImage',
              _id: '29348698236',
              cmsSettings: {
                isHidden: false,
                cmsTitle: 'menu-item-text-and-image',
              },
              sectionId: '2983tr7283g329',
              sectionBackground: 'dough',
              description:
                data?.textAndImageContent?.description || geti18nText(region, 'menuTextAndImageDefaultDescription'),
              title: data?.textAndImageContent?.title || geti18nText(region, 'menuTextAndImageDefaultTitle'),
              eyebrow: null,
              media: {
                image: data?.textAndImageContent?.image || data?.menuItemData?.images?.[0],
                mediaStyle: isArch ? 'arch' : 'roundedCorners',
                mediaType: 'image',
                videoLoopDesktop: null,
                videoLoopMobile: null,
              },
              textPosition: 'right',
              cta: defaultLink,
            },
          ],
        }

        newSections.push(textAndImageData)
      }
    }

    if (data._type === 'location') {
      const locationHero = {
        section: [
          {
            _type: 'locationHero',
            _id: 'locationHero',
            areaTitle: data?.locationData?.areaTitle || '',
            city: data?.locationData?.city || '',
            cmsSettings: {
              isHidden: false,
              cmsTitle: '',
            },
            emailSubscribeText: data?.locationData?.emailSubscribeText || '',
            eyebrowText: data?.locationData?.eyebrowText || false,
            featuredImage: data?.locationData?.featuredImage || undefined,
            googleMapsLink: data?.locationData?.googleMapsLink || '',
            hasCatering: data?.locationData?.hasCatering || false,
            cateringLink: data?.locationData?.cateringLink || undefined,
            hasPrivateDining: data?.locationData?.hasPrivateDining || undefined,
            heroDescription: data?.locationData?.heroDescription || [],
            holidayHours: data?.locationData?.holidayHours || [],
            hours: (() => {
              try {
                return typeof data?.locationData?.hours === 'string'
                  ? JSON.parse(data?.locationData?.hours)
                  : data?.locationData?.hours
              } catch (e) {
                console.error('Failed to parse location hours:', e)
                return data?.locationData?.hours || {}
              }
            })(),
            images: data?.locationData?.images || [],
            isComingSoon: data?.locationData?.isComingSoon || false,
            isReservationOnly: data?.locationData?.isReservationOnly || false,
            isSoftOpening: data?.locationData?.isSoftOpening || false,
            isTemporarilyClosed: data?.locationData?.isTemporarilyClosed || false,
            isNew: data?.locationData?.isNew || false,
            phoneNumber: data?.locationData?.phoneNumber || '',
            postalCode: data?.locationData?.postalCode || '',
            province: data?.locationData?.province || '',
            reservationLinks: data?.locationData?.reservationLinks || [],
            sectionBackground: 'dough' as SectionBackground,
            serviceLinks: data?.locationData?.serviceLinks || [],
            slug: data?.slug?.current || '',
            state: data?.locationData?.state || '',
            streetAddress: data?.locationData?.streetAddress || '',
            timeZone: data?.locationData?.timeZone || '',
            title: data?.title || '',
            weeklyOpeningHours: data?.locationData?.weeklyOpeningHours,
          },
        ],
      }

      newSections.push(locationHero)
    }

    if (data._type === 'blogPost') {
      const blogPostHero = {
        section: [
          {
            _type: 'blogPost',
            _id: 'blogPost',
            cmsSettings: {
              isHidden: false,
              cmsTitle: '',
            },
            sectionBackground: 'dough' as SectionBackground,
            title: data?.title || '',
            richTextContent: data?.richTextContent,
          },
        ],
      }

      newSections.push(blogPostHero)
    }

    if (!newSections.length) return sectionsByRegion

    const sections: SectionsSchema = [...newSections]

    if (sectionsByRegion?.length) {
      sectionsByRegion.forEach(sectionItem => {
        sections.push(sectionItem)
      })
    }

    const filteredSections = sections.filter(section => section?.section?.[0] && Object.keys(section.section[0]).length)

    return filteredSections
  }

  return sectionsByRegion
}
